import { Button, FormField, FormGrid, Text } from '@dixa/lib-shared-habla';
import {
  getWidgetDefinitions,
  PairingProvider,
} from 'lib-integrations-partner-widgets';
import { useMemo } from 'react';

import { PartnerSettingsConfigurationSchema } from 'apis/partnerIntegrations';
import { useInitiatePairingFromDixa } from 'hooks/useInitiatePairingFromDixa';

const widgetDefinitions = getWidgetDefinitions();

type ThirdPartyMarketplaceAppSetupProps = {
  integrationType: string;
  field: PartnerSettingsConfigurationSchema;
};

/**
 * The step for integrations that are created by installing the marketplace
 * app and pairing it with Dixa, so instead of a form the user is sent to the
 * marketplace app. The widget definition can provide its own banner, otherwise
 * a plain link to the install URL from the schema is shown.
 *
 * @param integrationType - The type of the integration.
 * @param field - The schema field of type `marketplaceApp`.
 */
export const ThirdPartyMarketplaceAppSetup = ({
  integrationType,
  field,
}: ThirdPartyMarketplaceAppSetupProps): JSX.Element => {
  const initiateFromDixa = useInitiatePairingFromDixa(integrationType);

  const pairingContextValue = useMemo(
    () => ({ initiateFromDixa }),
    [initiateFromDixa],
  );

  const { MarketplaceAppSetupBanner } =
    widgetDefinitions[integrationType] ?? {};
  const installUrl = field.additionalProperties?.installUrl as
    | string
    | undefined;

  return (
    <PairingProvider value={pairingContextValue}>
      <FormGrid>
        {MarketplaceAppSetupBanner ? (
          <MarketplaceAppSetupBanner />
        ) : (
          <FormField>
            <Text.Heading variant="heading-2">{field.label}</Text.Heading>
            {installUrl && (
              <Button
                icon="external-link"
                variant="secondary"
                onClick={() =>
                  window.open(installUrl, '_blank', 'noopener,noreferrer')
                }
              >
                Open the marketplace app
              </Button>
            )}
          </FormField>
        )}

        <FormField>
          <Text.Paragraph subdued>
            Once the integration is connected, it can be selected from the list
            of credentials.
          </Text.Paragraph>
        </FormField>
      </FormGrid>
    </PairingProvider>
  );
};
